import { CaptionFrameView } from "@cva/renderer";
import {
  bindColor,
  bindTextStyle,
  CAPTION_ANIMATIONS,
  type CaptionAnimation,
  type CaptionPreset,
  type CaptionRow,
  type CaptionStyle,
  type CaptionsElement,
  captionLayoutSchema,
  captionRowText,
  captionSplitWord,
  captionStyleSchema,
  captionWordsInSelection,
  closestWeight,
  editCaptionText,
  formatTimecode,
  getFontFamily,
  type TimelineWord,
  type TranscriptInfo,
  WEIGHT_LABELS,
} from "@cva/shared";
import {
  AlignCenterVertical,
  AlignEndVertical,
  AlignStartVertical,
  Captions,
  CaseUpper,
  ChevronRight,
  Eye,
  EyeOff,
  Highlighter,
  Loader2,
  Palette,
  Pilcrow,
  RotateCcw,
  Scissors,
  TextAlignCenter,
  TextAlignEnd,
  TextAlignStart,
  Type,
  Wand2,
} from "lucide-react";
import {
  memo,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import { Select } from "../../components/ui/input";
import { estimateProgress, useElapsed } from "../../lib/activity";
import { cn } from "../../lib/utils";
import { type BrandLink, ColorButton } from "../ColorPicker";
import { useEnv } from "../env";
import { FontPicker } from "../FontPicker";
import { type EditorStore, useEditor, useEditorApi } from "../store";
import { IconButton, NumberField, Row, Section, Segmented, Slider, Toggle } from "../ui";
import {
  applyCaptions,
  applyPreset,
  applyWordOverrides,
  captionPresets,
  cutWords,
  resetWords,
  setCaptionsHidden,
  setWordOverride,
  setWordsHidden,
  type WordPatches,
} from "./commands";
import { useCaptions } from "./data";
import { EmphasisBar, EmphasisStyleFields, toggleEmphasis } from "./Emphasis";
import { TranscriptStatus } from "./Properties";
import { type CaptionTab, useVideo, useVideoApi, type VideoStore } from "./store";

/**
 * Captions in the editor: the timeline shows them as a track, selecting it
 * opens this inspector in the right panel. "Text" lists every caption to read
 * along and correct, "Stil" holds the look.
 */

const percent = (value: number) => `${Math.round(value * 100)} %`;

const CAPTION_ANIMATION_LABELS: Record<CaptionAnimation, string> = {
  none: "Ohne",
  fade: "Einblenden",
  "slide-up": "Von unten",
  pop: "Pop (Wort für Wort)",
  "word-by-word": "Einzelne Wörter",
  typewriter: "Schreibmaschine",
  karaoke: "Karaoke",
  bounce: "Hüpfen",
};

function FieldLabel({ children }: { children: ReactNode }) {
  return <span className="w-24 shrink-0 text-xs text-zinc-500">{children}</span>;
}

/** Video files of the main track (each has its own transcript). */
export function useCaptionVideoAssets(): string[] {
  const clips = useEditor((s) => s.doc.timeline?.clips);
  return useMemo(
    () => [
      ...new Set((clips ?? []).filter((clip) => clip.kind === "video").map((clip) => clip.assetId)),
    ],
    [clips],
  );
}

/** Whether a video still needs attention before its captions are complete. */
export function transcriptPending(
  info: TranscriptInfo | null | undefined,
  hasAudio: boolean | undefined,
) {
  if (hasAudio === false) return false;
  return info?.status !== "done";
}

/** Selects the captions element and opens the inspector (optionally at a caption). */
export function openCaptions(
  store: EditorStore,
  video: VideoStore,
  options: { tab?: CaptionTab; focus?: { key: string; edit?: boolean } } = {},
) {
  const { doc } = store.getState();
  let id = Object.values(doc.elements).find((element) => element.type === "captions")?.id;
  if (!id) {
    if (store.getState().readOnly) return;
    id = applyCaptions(store);
    video.getState().setCaptionTab("style");
  }
  video.getState().selectClip(null);
  store.getState().select([id]);
  if (options.tab) video.getState().setCaptionTab(options.tab);
  if (options.focus) video.getState().focusCaption(options.focus.key, options.focus.edit);
}

/** Nothing selected: whether the video has captions, and the way to them. */
export function CaptionsSummary() {
  const store = useEditorApi();
  const video = useVideoApi()!;
  const env = useEnv();
  const brand = useEditor((s) => s.brand);
  const readOnly = useEditor((s) => s.readOnly);
  const { captionsElement, rows, infos } = useCaptions();
  const assetIds = useCaptionVideoAssets();
  const running = assetIds.some((id) => {
    const status = infos.get(id)?.status;
    return status === "queued" || status === "processing";
  });
  const failed = assetIds.some((id) => infos.get(id)?.status === "failed");
  const words = assetIds.reduce((sum, id) => sum + (infos.get(id)?.words.length ?? 0), 0);
  let state: string;
  if (captionsElement) {
    const preset = captionPresets(brand).find((entry) => entry.id === captionsElement.preset);
    state = [
      captionsElement.hidden ? "Ausgeblendet" : "Im Video",
      preset ? `Stil „${preset.label}“` : null,
      rows.length > 0 ? `${rows.length} Untertitel` : null,
    ]
      .filter(Boolean)
      .join(" · ");
  } else if (assetIds.length === 0) state = "Kommen automatisch aus dem Ton deiner Videos.";
  else if (running) state = "Die Sprache wird gerade erkannt – du kannst sie schon einblenden.";
  else if (failed && words === 0) state = "Die Spracherkennung ist fehlgeschlagen.";
  else if (words > 0) state = `${words} Wörter erkannt – bereit zum Einblenden.`;
  else state = "In den Videos wurde noch keine Sprache erkannt.";
  return (
    <Section title="Untertitel">
      <p className="flex items-center gap-1.5 text-xs text-zinc-500">
        {running && !captionsElement ? <Loader2 className="size-3 animate-spin" /> : null}
        {state}
      </p>
      {captionsElement ? (
        <Row>
          <button
            type="button"
            onClick={() => openCaptions(store, video, { tab: "text" })}
            className="inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md border border-zinc-200 text-xs font-medium text-zinc-700 hover:bg-zinc-50 [&_svg]:size-3.5"
          >
            <Type /> Text
          </button>
          <button
            type="button"
            onClick={() => openCaptions(store, video, { tab: "style" })}
            className="inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md border border-zinc-200 text-xs font-medium text-zinc-700 hover:bg-zinc-50 [&_svg]:size-3.5"
          >
            <Palette /> Stil
          </button>
        </Row>
      ) : (
        <button
          type="button"
          disabled={readOnly || assetIds.length === 0}
          onClick={() => openCaptions(store, video)}
          className="inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-md bg-violet-600 text-xs font-medium text-white hover:bg-violet-700 disabled:opacity-50 [&_svg]:size-3.5"
        >
          <Captions /> Untertitel einblenden
        </button>
      )}
      {failed && !captionsElement
        ? assetIds
            .filter((id) => infos.get(id)?.status === "failed")
            .map((id) => (
              <div key={id} className="rounded-lg border border-zinc-200 p-2.5">
                <p className="mb-1 truncate text-xs font-medium text-zinc-800">
                  {env.assets.get(id)?.name ?? "Video"}
                </p>
                <TranscriptStatus assetId={id} />
              </div>
            ))
        : null}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Inspector
// ---------------------------------------------------------------------------

const TABS: { id: CaptionTab; label: string; icon: ReactNode; title: string }[] = [
  { id: "text", label: "Text", icon: <Type />, title: "Untertitel mitlesen und korrigieren" },
  { id: "style", label: "Stil", icon: <Palette />, title: "Aussehen und Animation" },
];

export function CaptionInspector({
  element,
  styleExtras,
}: {
  element: CaptionsElement;
  /** Brand links and position, from the generic properties panel. */
  styleExtras: { before: ReactNode; after: ReactNode };
}) {
  const store = useEditorApi();
  const video = useVideoApi()!;
  const tab = useVideo((s) => s.captionTab);
  return (
    <>
      <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-zinc-100 bg-white px-4 py-2">
        <div role="tablist" className="flex flex-1 rounded-lg bg-zinc-100 p-0.5">
          {TABS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              role="tab"
              aria-selected={tab === entry.id}
              title={entry.title}
              onClick={() => video.getState().setCaptionTab(entry.id)}
              className={cn(
                "inline-flex h-7 flex-1 items-center justify-center gap-1.5 rounded-md text-xs font-medium text-zinc-600 [&_svg]:size-3.5",
                tab === entry.id && "bg-white text-zinc-900 shadow-sm",
              )}
            >
              {entry.icon}
              {entry.label}
            </button>
          ))}
        </div>
        <IconButton
          label={element.hidden ? "Untertitel im Video zeigen" : "Untertitel im Video ausblenden"}
          active={element.hidden}
          onClick={() => setCaptionsHidden(store, !element.hidden)}
        >
          {element.hidden ? <EyeOff /> : <Eye />}
        </IconButton>
      </div>
      {element.hidden ? (
        <p className="border-b border-amber-100 bg-amber-50 px-4 py-2 text-xs text-amber-800">
          Ausgeblendet – die Untertitel erscheinen gerade nicht im Video und im Export.
        </p>
      ) : null}
      {tab === "text" ? (
        <CaptionTextTab />
      ) : (
        <>
          <PresetGallery element={element} />
          {styleExtras.before}
          <CaptionStyleFields element={element} />
          {styleExtras.after}
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// "Text": every caption, read along while playing, corrected like plain text
// ---------------------------------------------------------------------------

function rowElement(list: HTMLElement | null, key: string) {
  return list?.querySelector<HTMLElement>(`[data-row="${CSS.escape(key)}"]`);
}

function rowIndexAt(rows: readonly CaptionRow[], t: number) {
  return rows.findIndex((row) => row.page && t >= row.page.start && t < row.page.end);
}

function CaptionTextTab() {
  const video = useVideoApi()!;
  const env = useEnv();
  const readOnly = useEditor((s) => s.readOnly);
  const { rows, infos } = useCaptions();
  const assetIds = useCaptionVideoAssets();
  const activeIndex = useVideo((s) => rowIndexAt(rows, s.time));
  const playing = useVideo((s) => s.playing);
  const focus = useVideo((s) => s.captionFocus);
  // The caption being edited, by one of its words: saving another caption may regroup the
  // words, the clicked word stays the same. `atWord`: cursor behind it, else at the end.
  const [editing, setEditing] = useState<{ word: string; atWord: boolean } | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const list = useRef<HTMLDivElement>(null);
  const editingRow = editing
    ? rows.find((row) => row.words.some((word) => word.key === editing.word))
    : undefined;
  const startEditing = useCallback((word: string) => setEditing({ word, atWord: true }), []);
  // Another caption may already be open again (the click on it closed this one).
  const stopEditing = useCallback(
    (words: readonly TimelineWord[]) =>
      setEditing((current) =>
        current && words.some((word) => word.key === current.word) ? null : current,
      ),
    [],
  );

  // Requests from the timeline and the canvas: show (and edit) a caption.
  useEffect(() => {
    if (!focus) return;
    const row = rows.find((entry) => entry.words.some((word) => word.key === focus.key));
    if (!row) return;
    video.getState().clearCaptionFocus();
    setFlash(row.key);
    if (focus.edit && !readOnly) setEditing({ word: row.key, atWord: false });
    requestAnimationFrame(() =>
      rowElement(list.current, row.key)?.scrollIntoView({ block: "center" }),
    );
  }, [focus, rows, video, readOnly]);

  useEffect(() => {
    if (!flash) return;
    const timer = setTimeout(() => setFlash(null), 1400);
    return () => clearTimeout(timer);
  }, [flash]);

  // Read along while playing.
  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll whenever the spoken caption changes
  useEffect(() => {
    if (!playing || editing || activeIndex < 0) return;
    const row = rows[activeIndex];
    if (row) rowElement(list.current, row.key)?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, playing, editing]);

  const pending = assetIds.filter((id) =>
    transcriptPending(infos.get(id), env.assets.get(id)?.media?.hasAudio),
  );
  const recognition =
    assetIds.length > 0 ? <RecognitionList assetIds={assetIds} open={pending.length > 0} /> : null;

  if (assetIds.length === 0) {
    return (
      <p className="p-4 text-xs text-zinc-500">
        Füge ein Video mit Ton hinzu – die Sprache wird automatisch auf dem Server erkannt (Deutsch
        und Englisch), ganz ohne externe Dienste.
      </p>
    );
  }
  return (
    // Room at the end for the activity button in the corner.
    <div className="pb-12">
      {pending.length > 0 ? recognition : null}
      {rows.length === 0 ? (
        <p className="p-4 text-xs text-zinc-500">
          {pending.length > 0
            ? "Die Untertitel erscheinen hier, sobald die Sprache erkannt ist. Du kannst solange weiter schneiden."
            : "In den Videos wurden keine gesprochenen Wörter erkannt."}
        </p>
      ) : (
        <>
          {!readOnly ? (
            <p className="px-4 pt-3 text-[11px] leading-4 text-zinc-500">
              Klick auf ein Wort springt hin und öffnet den Text zum Korrigieren. Gelöschte Wörter
              werden ausgeblendet.
            </p>
          ) : null}
          {!readOnly ? <EmphasisBar /> : null}
          <div ref={list} className="space-y-px p-2">
            {rows.map((row, index) => (
              <CaptionRowItem
                key={row.key}
                row={row}
                active={index === activeIndex}
                flash={flash === row.key}
                editing={
                  editing && editingRow === row ? (editing.atWord ? editing.word : "") : null
                }
                readOnly={readOnly}
                onEdit={startEditing}
                onDone={stopEditing}
              />
            ))}
          </div>
          <p className="flex items-center gap-1 px-4 pb-3 text-[11px] text-zinc-400">
            <Wand2 className="size-3" /> Wellig unterstrichen: unsicher erkannt – kurz prüfen.
          </p>
        </>
      )}
      {pending.length === 0 ? recognition : null}
    </div>
  );
}

/** Language and state of the recognition per video. */
function RecognitionList({ assetIds, open }: { assetIds: string[]; open: boolean }) {
  const env = useEnv();
  const [expanded, setExpanded] = useState(open);
  useEffect(() => {
    if (open) setExpanded(true);
  }, [open]);
  return (
    <section className="border-b border-zinc-100 px-4 py-3 last:border-b-0">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center gap-1 text-xs font-semibold text-zinc-700"
      >
        <ChevronRight className={cn("size-3.5 transition", expanded && "rotate-90")} />
        Spracherkennung
        <span className="ml-auto font-normal tracking-normal normal-case">
          {assetIds.length === 1 ? "1 Video" : `${assetIds.length} Videos`}
        </span>
      </button>
      {expanded ? (
        <div className="mt-2.5 space-y-2">
          {assetIds.map((assetId) => (
            <div key={assetId} className="rounded-lg border border-zinc-200 p-2.5">
              <p className="mb-1 truncate text-xs font-medium text-zinc-800">
                {env.assets.get(assetId)?.name ?? "Video"}
              </p>
              <TranscriptStatus assetId={assetId} />
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

/** Character offset of the end of each visible word in `captionRowText`. */
function wordEnds(words: readonly TimelineWord[]) {
  const ends = new Map<string, number>();
  let offset = 0;
  for (const word of words) {
    if (word.hidden) continue;
    const text = word.text.trim();
    if (!text) continue;
    if (offset > 0) offset += 1;
    offset += text.length;
    ends.set(word.key, offset);
  }
  return ends;
}

const CaptionRowItem = memo(function CaptionRowItem({
  row,
  active,
  flash,
  editing,
  readOnly,
  onEdit,
  onDone,
}: {
  row: CaptionRow;
  active: boolean;
  flash: boolean;
  /** Open for editing: the word to put the cursor behind ("" for the end). */
  editing: string | null;
  readOnly: boolean;
  onEdit: (word: string) => void;
  onDone: (words: readonly TimelineWord[]) => void;
}) {
  const store = useEditorApi();
  const video = useVideoApi()!;
  // Only the caption being spoken follows the playhead word by word.
  const activeWord = useVideo((s) =>
    active
      ? (row.words.find((word) => !word.hidden && s.time >= word.start && s.time < word.end)?.key ??
        null)
      : null,
  );
  const hidden = row.page === null;
  const first = row.words[0];
  const changed = row.words.some((word) => word.hidden || word.text !== word.original);
  const open = (word: TimelineWord) => {
    video.getState().seek(word.start);
    if (!readOnly) onEdit(word.key);
  };
  return (
    <div
      data-row={row.key}
      className={cn(
        "group scroll-mt-14 rounded-lg border border-transparent px-2 py-1.5 transition-colors",
        editing === null && "hover:bg-zinc-50",
        active && editing === null && "bg-amber-50 hover:bg-amber-50",
        flash && "border-brand-300 bg-brand-50",
        editing !== null && "border-brand-300 bg-white shadow-sm",
      )}
    >
      <div className="flex h-4 items-center gap-1.5 text-[10px] text-zinc-400">
        <button
          type="button"
          className="tabular-nums hover:text-brand-700"
          title="Zu diesem Untertitel springen"
          onClick={() => video.getState().seek(row.start)}
        >
          {formatTimecode(row.start)}
        </button>
        {first?.breakBefore ? (
          <button
            type="button"
            disabled={readOnly}
            title={
              readOnly
                ? "Beginnt von Hand hier"
                : "Beginnt von Hand hier – klicken, um wieder mit dem vorherigen Untertitel zu verbinden"
            }
            onClick={() => setWordOverride(store, first.key, { breakBefore: undefined })}
            className="inline-flex items-center gap-0.5 rounded text-violet-500 enabled:hover:text-violet-800"
          >
            <Pilcrow className="size-3" />
            {readOnly ? null : <span className="hidden group-hover:inline">verbinden</span>}
          </button>
        ) : null}
        {hidden ? <span>ausgeblendet</span> : null}
        {changed && !readOnly && editing === null ? (
          <button
            type="button"
            className="ml-auto hidden items-center gap-0.5 hover:text-zinc-700 group-hover:inline-flex"
            title="Erkannten Text wiederherstellen"
            onClick={() => resetWords(store, row.words)}
          >
            <RotateCcw className="size-3" /> Original
          </button>
        ) : null}
      </div>
      {editing !== null ? (
        <RowEditor
          // Other words than when it opened (another caption was saved): start over.
          key={row.words.map((word) => word.key).join(" ")}
          row={row}
          caretWord={editing || null}
          onDone={() => onDone(row.words)}
        />
      ) : (
        <p className="text-sm leading-6 text-zinc-800">
          {row.words.map((word, index) => {
            const gap = index > 0 ? " " : null;
            if (word.hidden) {
              return (
                <span key={word.key}>
                  {gap}
                  <button
                    type="button"
                    disabled={readOnly}
                    title={`Ausgeblendet: „${word.original}“${readOnly ? "" : " – klicken zum Einblenden"}`}
                    onClick={() => setWordsHidden(store, [word], false)}
                    className="rounded text-zinc-400 line-through decoration-zinc-300 hover:bg-zinc-100 hover:text-zinc-600"
                  >
                    {word.original}
                  </button>
                </span>
              );
            }
            const uncertain = (word.confidence ?? 1) < 0.5;
            return (
              <span key={word.key}>
                {gap}
                <button
                  type="button"
                  // On press, not on click: the caption open before saves when it closes and
                  // may move this word elsewhere before a click would arrive.
                  onMouseDown={(event) => {
                    if (event.button !== 0) return;
                    event.preventDefault();
                    open(word);
                  }}
                  onClick={(event) => {
                    if (event.detail === 0) open(word);
                  }}
                  title={`${formatTimecode(word.start)}${word.text !== word.original ? ` · erkannt: „${word.original}“` : ""}${uncertain ? " · unsicher erkannt" : ""}`}
                  className={cn(
                    "rounded px-px text-left hover:bg-zinc-200/60",
                    word.key === activeWord && "bg-amber-200/80",
                    word.emphasis && "font-semibold text-brand-700",
                    word.text !== word.original &&
                      "underline decoration-brand-400 decoration-dotted",
                    uncertain && "underline decoration-amber-500 decoration-wavy",
                  )}
                >
                  {word.text}
                </button>
              </span>
            );
          })}
        </p>
      )}
    </div>
  );
});

/** The text of one caption as a plain text field, with the actions that need a cursor. */
function RowEditor({
  row,
  caretWord,
  onDone,
}: {
  row: CaptionRow;
  /** The cursor goes behind this word (null: at the end). */
  caretWord: string | null;
  onDone: () => void;
}) {
  const store = useEditorApi();
  const initial = useMemo(() => captionRowText(row.words), [row.words]);
  const [value, setValue] = useState(initial);
  const [selection, setSelection] = useState<[number, number]>([0, 0]);
  const field = useRef<HTMLTextAreaElement>(null);
  // Set when an action already saved (or dropped) the text, so leaving the field does nothing.
  const handled = useRef(false);
  const latest = useRef({ value, words: row.words });
  latest.current = { value, words: row.words };
  // Closed without leaving the field (a word of another caption was pressed): keep the text.
  useEffect(
    () => () => {
      if (handled.current) return;
      applyWordOverrides(
        store,
        editCaptionText(latest.current.words, latest.current.value).patches,
      );
    },
    [store],
  );

  const resize = () => {
    const element = field.current;
    if (!element) return;
    element.style.height = "auto";
    element.style.height = `${element.scrollHeight}px`;
  };
  // biome-ignore lint/correctness/useExhaustiveDependencies: only when editing starts
  useLayoutEffect(() => {
    const element = field.current;
    if (!element) return;
    resize();
    element.focus({ preventScroll: true });
    const at = (caretWord && wordEnds(row.words).get(caretWord)) || element.value.length;
    element.setSelectionRange(at, at);
    setSelection([at, at]);
  }, []);

  const patches = (text = value): Record<string, NonNullable<WordPatches[string]>> => ({
    ...editCaptionText(row.words, text).patches,
  });
  const finish = () => {
    handled.current = true;
    field.current?.blur();
  };
  const readSelection = (): [number, number] => {
    const element = field.current;
    return element ? [element.selectionStart, element.selectionEnd] : selection;
  };
  /** Marked words; null without a selection (then the whole caption is meant). */
  const selectedWords = () => {
    const [start, end] = readSelection();
    if (start === end) return null;
    const words = captionWordsInSelection(row.words, value, start, end);
    // Only spaces marked: nothing to act on, and the text stays in the field.
    if (words.length === 0) toast.info("Markiere die Wörter, um die es geht.");
    return words;
  };

  const split = () => {
    const [start] = readSelection();
    const word = captionSplitWord(row.words, value, start);
    if (!word) {
      toast.info("Setze den Cursor vor das Wort, mit dem der neue Untertitel beginnen soll.");
      return;
    }
    const next = patches();
    next[word.key] = { ...next[word.key], breakBefore: true };
    applyWordOverrides(store, next);
    finish();
  };
  const hide = () => {
    const words = selectedWords() ?? row.words.filter((word) => !word.hidden);
    if (words.length === 0) return;
    const next = patches();
    for (const word of words) next[word.key] = { ...next[word.key], hidden: true };
    applyWordOverrides(store, next);
    finish();
  };
  const emphasize = () => {
    const words = selectedWords();
    if (!words) {
      toast.info("Markiere die Wörter, die hervorgehoben werden sollen.");
      return;
    }
    if (words.length === 0) return;
    toggleEmphasis(store, words, patches());
    finish();
  };
  const cut = () => {
    const words = selectedWords() ?? row.words;
    if (words.length === 0) return;
    const removed = cutWords(store, words, patches());
    finish();
    if (removed > 0) toast.success("Aus dem Video geschnitten – ⌘Z macht es rückgängig");
    else {
      // Speaker clips on a track above (circle, split screen) are not cut by their text.
      toast.info(
        "Über den Text schneiden geht nur auf der Hauptspur. Wörter ausblenden geht auch hier.",
      );
    }
  };

  const hasSelection = selection[0] !== selection[1];
  // Buttons keep the focus (and the selection) in the text field.
  const keepFocus = (event: React.MouseEvent) => event.preventDefault();
  const action = (
    label: string,
    title: string,
    icon: ReactNode,
    onClick: () => void,
    danger?: boolean,
  ) => (
    <button
      type="button"
      title={title}
      onMouseDown={keepFocus}
      onClick={onClick}
      className={cn(
        "inline-flex h-6 items-center gap-1 rounded-md px-1.5 text-[11px] text-zinc-600 hover:bg-zinc-100 [&_svg]:size-3",
        danger && "text-red-700 hover:bg-red-50",
      )}
    >
      {icon}
      {label}
    </button>
  );
  return (
    <div>
      <textarea
        ref={field}
        value={value}
        rows={1}
        spellCheck
        lang="de"
        onChange={(event) => {
          setValue(event.target.value.replace(/\n/g, " "));
          resize();
        }}
        onSelect={(event) =>
          setSelection([event.currentTarget.selectionStart, event.currentTarget.selectionEnd])
        }
        onKeyDown={(event) => {
          if (event.key === "Enter" && event.shiftKey) {
            event.preventDefault();
            split();
          } else if (event.key === "Enter") {
            event.preventDefault();
            field.current?.blur();
          } else if (event.key === "Escape") {
            event.preventDefault();
            finish();
          }
        }}
        onBlur={() => {
          if (!handled.current) {
            handled.current = true;
            applyWordOverrides(store, patches());
          }
          onDone();
        }}
        className="block w-full resize-none overflow-hidden rounded-md bg-transparent text-sm leading-6 text-zinc-900 outline-none"
      />
      <div className="mt-1 flex flex-wrap items-center gap-0.5 border-t border-zinc-100 pt-1">
        {action("Teilen", "Neuer Untertitel ab dem Cursor (⇧↵)", <Pilcrow />, split)}
        {action(
          "Ausblenden",
          hasSelection ? "Markierte Wörter ausblenden" : "Ganzen Untertitel ausblenden",
          <EyeOff />,
          hide,
        )}
        {action(
          "Hervorheben",
          "Markierte Wörter als Schlüsselwort hervorheben (nochmal: aufheben)",
          <Highlighter />,
          emphasize,
        )}
        {action(
          "Schneiden",
          hasSelection
            ? "Markierte Wörter aus dem Video schneiden"
            : "Diesen Untertitel aus dem Video schneiden",
          <Scissors />,
          cut,
          true,
        )}
      </div>
      <p className="mt-0.5 text-[10px] text-zinc-400">↵ übernehmen · Esc verwerfen</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Timeline: state of the recognition where the captions of a clip will appear
// ---------------------------------------------------------------------------

export function TranscribingLabel({ info }: { info: TranscriptInfo }) {
  const elapsed = useElapsed(info.status === "processing" ? info.elapsedMs : null);
  if (info.status === "queued") {
    return (
      <>
        <Loader2 className="size-3 shrink-0 animate-spin" />
        <span className="truncate">
          {info.queuePosition ? `Wartet (${info.queuePosition} vor dir)` : "Wartet auf den Start"}
        </span>
      </>
    );
  }
  const { progress } = estimateProgress(info.progress, elapsed, info.estimatedMs);
  return (
    <>
      <span
        className="absolute inset-y-0 left-0 bg-violet-200/70 transition-[width] duration-1000"
        style={{ width: `${Math.floor(progress)}%` }}
      />
      <Loader2 className="relative size-3 shrink-0 animate-spin" />
      <span className="relative truncate">Sprache wird erkannt … {Math.floor(progress)} %</span>
    </>
  );
}

// ---------------------------------------------------------------------------
// "Stil": presets with a preview, then the details
// ---------------------------------------------------------------------------

const SAMPLE = "Tipps für Reels";

function PresetPreview({ preset }: { preset: CaptionPreset }) {
  const element = useMemo((): CaptionsElement => {
    const style = captionStyleSchema.parse({ ...preset.style });
    return {
      id: `preview-${preset.id}`,
      type: "captions",
      x: 0,
      y: 0,
      width: 600,
      height: 200,
      rotation: 0,
      opacity: 1,
      locked: false,
      hidden: false,
      style,
      captionAnimation: preset.animation,
      layout: captionLayoutSchema.parse(preset.layout ?? {}),
    };
  }, [preset]);
  const words = SAMPLE.split(" ").slice(0, preset.animation.preset === "word-by-word" ? 1 : 4);
  const frame = {
    page: { index: 0, start: 0, end: 1, lines: [] },
    opacity: 1,
    y: 0,
    lines: [
      words.map((text, index) => ({
        word: { key: `w${index}`, text, start: index, end: index + 1, breakBefore: false },
        visible: true,
        active: index === 1 || words.length === 1,
        spoken: index <= 1,
        opacity:
          preset.animation.preset === "karaoke" && index > 1 ? element.style.upcomingOpacity : 1,
        scale: index === 1 ? element.style.activeScale : 1,
        y: 0,
        reveal: null,
      })),
    ],
  };
  // One line at document size, scaled down to the tile (about 120 px).
  const width = 1000;
  const scale = 0.14;
  return (
    <div className="relative flex h-14 items-center justify-center overflow-hidden rounded-md bg-gradient-to-br from-zinc-600 to-zinc-900">
      <div
        style={{ width, transform: `scale(${scale})`, flexShrink: 0 }}
        className="flex justify-center whitespace-nowrap"
      >
        <CaptionFrameView element={element} frame={frame} />
      </div>
    </div>
  );
}

function PresetGallery({ element }: { element: CaptionsElement }) {
  const store = useEditorApi();
  const brand = useEditor((s) => s.brand);
  const presets = captionPresets(brand);
  return (
    <Section title="Vorlage">
      <div className="grid grid-cols-2 gap-2">
        {presets.map((preset) => (
          <button
            key={preset.id}
            type="button"
            title={preset.description}
            onClick={() =>
              store.getState().update((draft) => {
                const target = draft.elements[element.id];
                if (target?.type === "captions") applyPreset(target, preset, brand);
              })
            }
            className={cn(
              "space-y-1 rounded-lg border p-1 text-left transition hover:border-brand-300",
              element.preset === preset.id
                ? "border-brand-500 ring-1 ring-brand-500"
                : "border-zinc-200",
            )}
          >
            <PresetPreview preset={preset} />
            <span className="block px-0.5 text-[11px] font-medium text-zinc-800">
              {preset.label}
            </span>
          </button>
        ))}
      </div>
    </Section>
  );
}

function CaptionStyleFields({ element }: { element: CaptionsElement }) {
  const store = useEditorApi();
  const brand = useEditor((s) => s.brand);
  const style = element.style;
  const update = (recipe: (target: CaptionsElement) => void, merge?: string) =>
    store.getState().update(
      (draft) => {
        const target = draft.elements[element.id];
        if (target?.type === "captions") recipe(target);
      },
      merge ? { merge: `captions-${element.id}-${merge}` } : undefined,
    );
  const setStyle = (patch: Partial<CaptionStyle>, merge?: string) =>
    update((target) => Object.assign(target.style, patch), merge);
  const link = (slot: "color" | "fill" | "textStroke"): BrandLink | undefined =>
    brand
      ? {
          token: element.brand?.[slot],
          onToken: (key) =>
            update((target) => {
              bindColor(target, slot, key, brand);
            }),
        }
      : undefined;
  const family = getFontFamily(style.fontFamily);
  const weights = family?.weights ?? [400, 700, 800, 900];
  return (
    <>
      <Section title="Animation">
        <Select
          className="h-8 w-full"
          value={element.captionAnimation.preset}
          onChange={(event) =>
            update((target) => {
              target.captionAnimation.preset = event.target.value as CaptionAnimation;
            })
          }
        >
          {CAPTION_ANIMATIONS.map((preset) => (
            <option key={preset} value={preset}>
              {CAPTION_ANIMATION_LABELS[preset]}
            </option>
          ))}
        </Select>
        <Row>
          <FieldLabel>Tempo</FieldLabel>
          <Slider
            value={element.captionAnimation.duration}
            min={60}
            max={600}
            step={10}
            onChange={(value) =>
              update((target) => (target.captionAnimation.duration = value), "duration")
            }
          />
          <span className="w-12 shrink-0 text-right text-xs text-zinc-600 tabular-nums">
            {element.captionAnimation.duration} ms
          </span>
        </Row>
        <Row>
          <FieldLabel>Aktives Wort</FieldLabel>
          <Slider
            value={style.activeScale}
            min={1}
            max={1.4}
            step={0.01}
            onChange={(value) => setStyle({ activeScale: value }, "scale")}
          />
          <span className="w-12 shrink-0 text-right text-xs text-zinc-600 tabular-nums">
            {percent(style.activeScale)}
          </span>
        </Row>
        {element.captionAnimation.preset === "karaoke" ? (
          <Row>
            <FieldLabel>Kommende</FieldLabel>
            <Slider
              value={style.upcomingOpacity}
              min={0}
              max={1}
              step={0.05}
              onChange={(value) => setStyle({ upcomingOpacity: value }, "upcoming")}
            />
            <span className="w-12 shrink-0 text-right text-xs text-zinc-600 tabular-nums">
              {percent(style.upcomingOpacity)}
            </span>
          </Row>
        ) : null}
      </Section>
      <Section title="Schrift">
        <FontPicker
          value={style.fontFamily}
          onChange={(fontFamily) =>
            setStyle({ fontFamily, fontWeight: closestWeight(fontFamily, style.fontWeight) })
          }
        />
        <Row>
          <Select
            className="h-8 flex-1"
            value={String(style.fontWeight)}
            onChange={(event) => setStyle({ fontWeight: Number(event.target.value) })}
          >
            {weights.map((weight) => (
              <option key={weight} value={weight}>
                {WEIGHT_LABELS[weight] ?? weight}
              </option>
            ))}
          </Select>
          <NumberField
            className="w-24"
            label="Größe"
            value={style.fontSize}
            min={16}
            max={300}
            onChange={(fontSize) => setStyle({ fontSize }, "size")}
          />
          <IconButton
            label="Großbuchstaben"
            active={style.uppercase}
            onClick={() => setStyle({ uppercase: !style.uppercase })}
          >
            <CaseUpper />
          </IconButton>
        </Row>
        <Row>
          <FieldLabel>Farbe</FieldLabel>
          <ColorButton
            value={style.color}
            brandLink={link("color")}
            onChange={(color) => setStyle({ color })}
          />
        </Row>
        {brand ? (
          <button
            type="button"
            className="text-xs font-medium text-brand-700 hover:underline"
            onClick={() =>
              update((target) => {
                bindTextStyle(target, "heading-1", brand, 1);
                const accent = brand.colors.find((color) => color.key === "accent")
                  ? "accent"
                  : "primary";
                bindColor(target, "fill", accent, brand);
              })
            }
          >
            Schrift und Akzentfarbe der Marke übernehmen
          </button>
        ) : null}
      </Section>
      <Section title="Hervorhebung">
        <Segmented
          className="w-full"
          value={style.highlight}
          onChange={(highlight) => setStyle({ highlight })}
          options={[
            { value: "none", label: "Keine", title: "Keine Hervorhebung" },
            { value: "color", label: "Farbe", title: "Aktives Wort farbig" },
            { value: "box", label: "Box", title: "Aktives Wort auf farbiger Fläche" },
            { value: "underline", label: "Linie", title: "Aktives Wort unterstrichen" },
          ]}
        />
        {style.highlight !== "none" ? (
          <Row>
            <FieldLabel>Akzent</FieldLabel>
            <ColorButton
              value={style.highlightColor}
              brandLink={link("fill")}
              onChange={(highlightColor) => setStyle({ highlightColor })}
            />
            {style.highlight === "box" ? (
              <ColorButton
                label="Textfarbe auf der Box"
                value={style.highlightTextColor}
                onChange={(highlightTextColor) => setStyle({ highlightTextColor })}
              />
            ) : null}
          </Row>
        ) : null}
      </Section>
      <EmphasisStyleFields element={element} />
      <Section title="Kontur & Schatten">
        <Row>
          <FieldLabel>Kontur</FieldLabel>
          <Slider
            value={style.stroke?.width ?? 0}
            min={0}
            max={16}
            step={0.5}
            onChange={(width) =>
              setStyle(
                {
                  stroke:
                    width > 0 ? { color: style.stroke?.color ?? "#000000", width } : undefined,
                },
                "stroke",
              )
            }
          />
          {style.stroke && style.stroke.width > 0 ? (
            <ColorButton
              value={style.stroke.color}
              brandLink={link("textStroke")}
              onChange={(color) => setStyle({ stroke: { width: style.stroke?.width ?? 4, color } })}
            >
              <span />
            </ColorButton>
          ) : null}
        </Row>
        <Toggle
          checked={Boolean(style.shadow)}
          onChange={(on) =>
            setStyle({ shadow: on ? { x: 0, y: 4, blur: 16, color: "#00000080" } : undefined })
          }
          label="Schatten"
        />
      </Section>
      <Section title="Hintergrund">
        <Segmented
          className="w-full"
          value={style.background}
          onChange={(background) => setStyle({ background })}
          options={[
            { value: "none", label: "Kein", title: "Kein Hintergrund" },
            { value: "line", label: "Zeile", title: "Box hinter jeder Zeile" },
            { value: "word", label: "Wort", title: "Box hinter jedem Wort" },
            { value: "page", label: "Block", title: "Eine Box für die ganze Untertitel-Zeile" },
          ]}
        />
        {style.background !== "none" || style.highlight === "box" ? (
          <Row>
            {style.background !== "none" ? (
              <ColorButton
                value={style.backgroundColor}
                allowAlpha
                onChange={(backgroundColor) => setStyle({ backgroundColor })}
              />
            ) : null}
            <NumberField
              className="w-28"
              label="Radius"
              value={style.backgroundRadius}
              min={0}
              max={100}
              onChange={(backgroundRadius) => setStyle({ backgroundRadius }, "radius")}
            />
          </Row>
        ) : null}
      </Section>
      <Section title="Aufteilung">
        <Row>
          <NumberField
            label="Wörter/Zeile"
            value={element.layout.maxWordsPerLine}
            min={1}
            max={12}
            onChange={(value) =>
              update((target) => (target.layout.maxWordsPerLine = Math.round(value)), "words")
            }
          />
          <NumberField
            label="Zeilen"
            value={element.layout.maxLines}
            min={1}
            max={4}
            onChange={(value) =>
              update((target) => (target.layout.maxLines = Math.round(value)), "lines")
            }
          />
        </Row>
        <Row>
          <NumberField
            label="Zeichen/Zeile"
            value={element.layout.maxCharsPerLine}
            min={4}
            max={80}
            onChange={(value) =>
              update((target) => (target.layout.maxCharsPerLine = Math.round(value)), "chars")
            }
          />
          <NumberField
            label="Pause"
            unit="ms"
            step={50}
            value={element.layout.pauseMs}
            min={100}
            max={5000}
            onChange={(value) =>
              update((target) => (target.layout.pauseMs = Math.round(value)), "pause")
            }
          />
        </Row>
      </Section>
      <Section title="Ausrichtung">
        <Row>
          <Segmented
            value={style.align}
            onChange={(align) => setStyle({ align })}
            options={[
              { value: "left", label: <TextAlignStart />, title: "Links" },
              { value: "center", label: <TextAlignCenter />, title: "Mitte" },
              { value: "right", label: <TextAlignEnd />, title: "Rechts" },
            ]}
          />
          <Segmented
            value={style.verticalAlign}
            onChange={(verticalAlign) => setStyle({ verticalAlign })}
            options={[
              { value: "top", label: <AlignStartVertical />, title: "Oben im Rahmen" },
              { value: "middle", label: <AlignCenterVertical />, title: "Mittig im Rahmen" },
              { value: "bottom", label: <AlignEndVertical />, title: "Unten im Rahmen" },
            ]}
          />
        </Row>
      </Section>
    </>
  );
}
